import { formatDate, formatNumber } from '@angular/common';
import {
  BeneficiarioVM,
  ComprobanteEnvio,
  CotizacionVM,
  DestinoMeta,
  DestinoRemesa,
  EnvioOk,
  RielColombia,
} from '../models/remittance.model';
import {
  comisionEnDestino,
  etiquetaTasaMostrada,
  formatoComision,
  formatoTasa,
  subtotalEnDestino,
  tasaMostrada,
} from './remittance-rules';

/**
 * COMPROBANTE DE ENVÍO — uno solo para todos los rieles de salida.
 *
 * El flujo viejo tenía comprobante en dos pantallas de tres
 * (`FiatCalculatorComponent:882` y `ConvertUsdtVesComponent:706`): un texto
 * armado a mano, distinto en cada una, y sólo copiado al portapapeles. Al
 * unificar el cotizador esos textos se perdieron. Acá hay UNO, derivado del
 * view-model normalizado, así que sirve igual a Venezuela, Chile, Colombia
 * (Bre-B y bancario) y Perú, y a cualquier riel que se agregue después: si el
 * par cotiza, el comprobante sale.
 *
 * Es un módulo PURO —sin DI, sin HTTP, sin `Date.now()`— por la misma razón que
 * `remittance-rules`: lo que se le entrega al cliente como constancia de un
 * envío de dinero se testea, no se revisa a ojo.
 *
 * REGLA DURA: el comprobante no nombra al proveedor del riel. Para el cliente
 * el envío es de Aguacate. Los nombres de banco y los métodos vienen de la
 * libreta —y en los rieles internacionales esas etiquetas las escribe el
 * proveedor— así que toda etiqueta de origen externo pasa por
 * `ocultarProveedor()` antes de entrar al texto.
 */

/**
 * Nombres de proveedor que nunca pueden llegar al cliente. Se comparan con
 * límite de palabra: `\bvita\b` no marca "invita" ni "evita", que sí podrían
 * aparecer dentro de una etiqueta legítima.
 */
const PROVEEDORES_OCULTOS = /\b(vita|vita\s*wallet)\b/i;

/**
 * Etiqueta lista para mostrar, o `null` si nombra al proveedor.
 *
 * Se descarta la etiqueta COMPLETA en vez de recortarle la palabra: de
 * "Vita CO" quedaría "CO", que no le dice nada a nadie y además delata que
 * había algo tapado. Sin etiqueta, la línea simplemente no se imprime.
 */
export function ocultarProveedor(label: string | null | undefined): string | null {
  const txt = (label || '').trim();
  if (!txt) return null;
  return PROVEEDORES_OCULTOS.test(txt) ? null : txt;
}

/** Etiquetas que describen el MÉTODO de entrega, no el banco. */
const METODOS = /pago\s*m[oó]vil|bre-?b/i;

/**
 * Cómo se entrega el dinero. Colombia lo define el riel elegido en el paso 1
 * —no la libreta—, así que se resuelve acá y no se lee de los badges: un
 * beneficiario Bre-B y uno bancario pueden traer la misma etiqueta.
 */
export function metodoDe(
  destino: DestinoRemesa,
  rielCo: RielColombia,
  badges: string[],
): string | null {
  if (destino === 'CO') {
    return rielCo === 'COBREB' ? 'Bre-B' : 'Transferencia bancaria';
  }
  const metodo = badges.find((b) => METODOS.test(b));
  return ocultarProveedor(metodo);
}

/**
 * Banco destino a partir de los badges normalizados. Se saltea el badge que
 * describe el método —"Pago Móvil" no es un banco— y cualquiera que nombre al
 * proveedor. El primero que sobrevive es el banco: las tres libretas lo ponen
 * primero (`mapVenezuela`, `mapChile`, `mapVita`).
 */
export function bancoDe(badges: string[]): string | null {
  const banco = badges.find((b) => !METODOS.test(b) && ocultarProveedor(b));
  return ocultarProveedor(banco);
}

export interface DatosComprobante {
  envio: EnvioOk;
  cotizacion: CotizacionVM;
  beneficiario: BeneficiarioVM;
  destino: DestinoMeta;
  rielCo: RielColombia;
  /** Instante en que el backend confirmó el envío (epoch ms). */
  enviadoEn: number;
}

/**
 * Congela el comprobante del envío.
 *
 * El monto y la moneda de DESTINO salen de la respuesta del backend
 * (`envio.recibe`), no de la cotización: es lo que quedó aceptado. La tasa,
 * también del backend cuando la informa (`envio.tasaAplicada`, hoy el swap
 * USDT→VES); si no, la de la cotización. El monto de origen sale de la
 * cotización que se envió, que es lo que el usuario vio y confirmó.
 *
 * El subtotal y la comisión se derivan del recibe del BACKEND, no del de la
 * cotización. Si los dos difieren —el caso ambiguo, o un `recibeCop` distinto
 * en la respuesta— tomar la comisión de la cotización dejaría un comprobante
 * cuya resta no cierra contra el monto que imprime dos líneas más arriba.
 */
export function construirComprobante(d: DatosComprobante): ComprobanteEnvio {
  const c = d.cotizacion;
  const aplicada = Number(d.envio.tasaAplicada);
  const tasa = aplicada > 0 ? aplicada : tasaMostrada(c);
  return {
    referencia: d.envio.referencia,
    enviadoEn: d.enviadoEn,

    pais: d.destino.nombre,
    monedaOrigen: c.monedaOrigen,
    monedaDestino: d.envio.monedaDestino,
    monto: c.monto,
    recibe: d.envio.recibe,

    tasa,
    etiquetaTasa: aplicada > 0 ? 'Tasa aplicada' : etiquetaTasaMostrada(c),

    subtotal: c.sinComision
      ? null
      : subtotalEnDestino(c.monto, tasa, c.origen, c.destino),
    comision: c.sinComision
      ? null
      : comisionEnDestino(c.monto, d.envio.recibe, tasa, c.origen, c.destino),
    sinComision: c.sinComision,

    beneficiario: d.beneficiario.titulo,
    cuenta: d.beneficiario.subtitulo,
    banco: bancoDe(d.beneficiario.badges),
    metodo: metodoDe(c.destino, d.rielCo, d.beneficiario.badges),

    eta: c.etaLabel,

    fmtDestino: c.decimales,
    fmtTasa: formatoTasa(c.origen, c.destino),
    fmtComision: formatoComision(c.destino),
  };
}

/**
 * Texto del comprobante, listo para pegar en WhatsApp.
 *
 * Se conserva el formato del flujo viejo —título, emojis, una línea por dato—
 * porque es el que los clientes ya reconocen y el que sobrevive intacto a un
 * pegado en cualquier chat. Lo que cambia es que ahora sale del view-model, no
 * de un `template literal` por pantalla, y que incluye el número de operación:
 * el comprobante viejo no lo tenía, así que no servía para reclamar nada.
 *
 * El `locale` es el mismo que usa `DecimalPipe` en la plantilla (`LOCALE_ID`),
 * para que el texto no muestre los números con otra puntuación que la pantalla.
 *
 * Una línea sin dato no se imprime vacía: se omite. Un comprobante con
 * "Banco: —" parece roto.
 *
 * NO lleva el estado del envío. Lo que devuelve el backend es un código de su
 * máquina de estados —`POR_PROCESAR`— y un comprobante que se reenvía por chat
 * no es el lugar para exponerlo: no le dice nada al beneficiario y envejece mal
 * (el envío avanza, el texto copiado no). El estado vive en el historial, que
 * sí se actualiza.
 */
export function textoComprobante(c: ComprobanteEnvio, locale: string): string {
  const num = (v: number, fmt: string) => formatNumber(v, locale, fmt);
  const lineas: string[] = ['COMPROBANTE DE ENVÍO — Aguacate', ''];

  if (c.referencia != null && `${c.referencia}`.trim() !== '') {
    lineas.push(`🧾 Operación: #${c.referencia}`);
  }
  lineas.push(`📅 Fecha: ${formatDate(c.enviadoEn, 'dd/MM/yyyy HH:mm', locale)}`);
  lineas.push('');

  lineas.push(`💰 Enviado: ${num(c.monto, '1.0-2')} ${c.monedaOrigen}`);
  lineas.push(`💵 Recibe: ${num(c.recibe, c.fmtDestino)} ${c.monedaDestino}`);
  if (c.tasa != null) {
    lineas.push(
      `💱 ${c.etiquetaTasa}: 1 ${c.monedaOrigen} = ` +
        `${num(c.tasa, c.fmtTasa)} ${c.monedaDestino}`,
    );
  }
  if (c.sinComision) {
    lineas.push('🏷️ Comisión: sin comisión');
  } else if (c.comision != null) {
    // El subtotal va JUNTO a la comisión: sin él, un descuento en moneda de
    // destino queda colgado y el que lee el comprobante no puede rehacer la
    // cuenta (`subtotal − comisión = recibe`).
    if (c.subtotal != null) {
      lineas.push(`🧮 Subtotal: ${num(c.subtotal, c.fmtDestino)} ${c.monedaDestino}`);
    }
    lineas.push(
      `🏷️ Comisión: −${num(c.comision, c.fmtComision)} ${c.monedaDestino}`,
    );
  }
  lineas.push('');

  lineas.push(`🌎 Destino: ${c.pais}`);
  if (c.beneficiario) lineas.push(`👤 Beneficiario: ${c.beneficiario}`);
  if (c.cuenta) lineas.push(`#️⃣ Cuenta: ${c.cuenta}`);
  if (c.banco) lineas.push(`🏦 Banco: ${c.banco}`);
  if (c.metodo) lineas.push(`🚀 Método: ${c.metodo}`);
  if (c.eta) lineas.push(`⏱️ Llega en: ${c.eta}`);

  return lineas.join('\n').trim();
}
